import { useFormContext } from 'react-hook-form';
import {
  CardNumberElement as CardNumber,
  CardCvcElement as CardCvc,
  CardExpiryElement as CardExpiry,
} from '@stripe/react-stripe-js';
import { cx } from '@chakra-ui/utils';
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/react';

import { preferredNetwork } from '../../constants';
import { useCommonOptionsStore } from '../../store';
import { useTranslations } from '../../translations/useTranslations';
import { createLogLocal } from '../../utils/log-local';

import type { CardInputControlProps } from './types';

type InputType = 'card' | 'cvc' | 'expiry';

/**
 * Props for the StripeCreditCardInputControl component. Includes custom
 * validation and event handlers.
 */
type StripeCreditCardInputControlProps = Omit<
  CardInputControlProps,
  'onChange' | 'onBlur' | 'validate'
> & {
  onChange: (value: any, brand?: string) => any;
  onBlur: (value: any, brand?: string) => any;
  inputType: InputType;
  /**
   * Maps field names to their validation functions (which return a
   * string error message, or `true` no error present).
   */
  rules?: { validate: Record<string, (event: any) => string | true> };
};

const log = createLogLocal('StripeCreditCardInputControl.tsx');

/**
 * Input field representing a credit card input field.
 * Used for card number, CVC, and Expiry date.
 *
 * Wraps Stripe Elements.
 */
export const StripeCreditCardInputControl = ({
  id,
  name: fieldName,
  label,
  helper,
  error,
  isDisabled,
  isRequired,
  inputType,
  theme,
  onChange,
  onBlur,
  rules,
}: StripeCreditCardInputControlProps) => {
  const { register, setValue, setError, getValues, clearErrors, formState } =
    useFormContext();

  const lang = useCommonOptionsStore(state => state.lang);
  const [t] = useTranslations(lang);
  const validate = rules?.validate || {};

  const registerOptions = {
    validate: {
      nativeValidation: () => formState.errors[fieldName]?.message as string,
      required: (val: string) => !isRequired || !!val || t('requiredField'),
    },
  };

  register(fieldName, { ...registerOptions });

  const handleStripeChange = (event: any) => {
    log('handleStripeChange', 'event:', event);

    const validationError: string | boolean | undefined | null =
      validate?.[fieldName]?.(event) ?? null;
    log('handleStripeChange', 'validationError:', validationError);

    // Set error if Stripe element returns an error
    if (event.error?.message) {
      setValue(fieldName, undefined);
      setError(fieldName, {
        type: 'validation',
        message: event.error?.message,
      });
    }

    // Set error if no data is present in the field
    else if (event.empty) {
      setValue(fieldName, undefined);
      setError(fieldName, {
        type: 'validation',
        message: t('requiredField'),
      });
    }

    // Set error if validation error is returned from the validate function
    else if (typeof validationError === 'string') {
      setValue(fieldName, undefined);
      setError(fieldName, {
        type: 'validation',
        message: validationError,
      });
    }

    // Set value if data is valid.
    else {
      setValue(fieldName, true);
      if (fieldName === 'cardNumber' && event.brand) {
        setValue('brand', event.brand);
      }
      clearErrors(fieldName);
    }
    if (onChange) onChange(fieldName as unknown as any, event?.brand ?? '');
  };

  const handleStripeBlur = () => {
    if (!getValues()[fieldName]) {
      if (!formState.errors[fieldName]?.message) {
        setError(fieldName, {
          type: 'validation',
          message: formState.errors[fieldName]?.message || t('requiredField'),
        });
      }
    }
    if (onBlur) onBlur(fieldName as unknown as any);
  };

  const cardInputProps = (includeOptions?: 'no-options') => ({
    id,
    onChange: handleStripeChange,
    onBlur: handleStripeBlur,
    ...(includeOptions !== 'no-options' ? { options: theme } : {}),
  });

  return (
    <Box>
      <FormControl
        isInvalid={!!error}
        isDisabled={isDisabled}
        isRequired={isRequired}
        className={cx('InputContainer')}
      >
        <FormLabel className={cx('InputLabel')}>{label}</FormLabel>

        {inputType === 'card' ? (
          <CardNumber
            options={{
              ...theme,
              preferredNetwork,
            }}
            {...cardInputProps('no-options')}
          />
        ) : null}
        {inputType === 'cvc' ? <CardCvc {...cardInputProps()} /> : null}
        {inputType === 'expiry' ? <CardExpiry {...cardInputProps()} /> : null}

        {helper && (
          <FormHelperText className={cx('InputHelper')}>
            {helper}
          </FormHelperText>
        )}
        {error && (
          <FormErrorMessage className={cx('InputError')}>
            {error as any}
          </FormErrorMessage>
        )}
      </FormControl>
    </Box>
  );
};
